// src/components/Store/StoreResult.jsx
// Pantallas a las que Stripe regresa tras pagar (/store/success) o cancelar
// (/store/cancelled). Al pagar, confirmamos el pago con el backend (que lo
// consulta a Stripe) para marcar el pedido como pagado de inmediato; el
// webhook queda como respaldo.
import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearCart } from '../../utils/store';
import { usePaymentConfirmation } from '../../utils/payment';

const StoreResult = ({ success }) => {
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get('order');
  const payment = usePaymentConfirmation(success ? searchParams.get('session_id') : null);
  const processing = success && payment === 'processing';

  // Pagado: el carrito ya se compró. Cancelado: se conserva para reintentar.
  useEffect(() => {
    if (success) clearCart();
  }, [success]);

  const key = success ? 'storeSuccess' : 'storeCancelled';
  const accent = success ? '#3A6B4C' : '#9A0002';

  return (
    <div className="bg-[#EFE6DD] min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[#3A2A20]/10 p-8 text-center">
        <div
          className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full"
          style={{ backgroundColor: `${accent}1A` }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke={accent}
            strokeWidth={2.5}
            className="h-7 w-7"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={success ? 'M4.5 12.75l6 6 9-13.5' : 'M6 18L18 6M6 6l12 12'}
            />
          </svg>
        </div>

        {payment === 'checking' ? (
          <p className="text-sm text-[#3A2A20]/60">{t('paymentStatus.confirming')}</p>
        ) : processing ? (
          <>
            <h1 className="font-['Fraunces'] text-2xl sm:text-3xl font-semibold text-[#3A2A20]">
              {t('paymentStatus.processingTitle')}
            </h1>
            <p className="text-[#3A2A20]/70 text-sm mt-3 leading-relaxed">{t('paymentStatus.processingMessage')}</p>
          </>
        ) : (
          <>
            <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: accent }}>
              {t(`${key}.eyebrow`)}
            </span>
            <h1 className="mt-2 font-['Fraunces'] text-2xl sm:text-3xl font-semibold text-[#3A2A20]">
              {t(`${key}.title`)}
            </h1>
            <p className="text-[#3A2A20]/70 text-sm mt-3 leading-relaxed">{t(`${key}.message`)}</p>
          </>
        )}
        {success && orderNumber && (
          <p className="mt-4 text-sm text-[#3A2A20]">
            {t('storeSuccess.orderNumber')} <strong>{orderNumber}</strong>
          </p>
        )}

        <Link
          to="/store"
          className="mt-6 inline-block w-full px-6 py-3.5 bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] rounded-xl text-sm font-medium transition"
        >
          {t(`${key}.backToStore`)}
        </Link>
      </div>
    </div>
  );
};

export default StoreResult;
